import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types/auth';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingEmail: string | null;
  login: (response: AuthResponse, rememberMe?: boolean) => void;
  setPendingMFA: (email: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (token) {
        try {
          const userData = await authService.me();
          setUser(userData);
        } catch (error: any) {
          if (error.response?.status === 401) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('refresh_token');
          } else {
            console.error('Error temporal al verificar sesión (servidor posiblemente apagado):', error);
          }
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = (response: AuthResponse, rememberMe: boolean = false) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    if (response.access_token) {
      storage.setItem('access_token', response.access_token);
    }
    if (response.refresh_token) {
      storage.setItem('refresh_token', response.refresh_token);
    }
    if (response.user) {
      setUser(response.user);
    }
    setPendingEmail(null);
  };

  const setPendingMFA = (email: string) => {
    setPendingEmail(email);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Ignorar error en logout
    }
    setUser(null);
    setPendingEmail(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        pendingEmail,
        login,
        setPendingMFA,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
